/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import { VUELTO_NEGOCIO_ICON } from '../../../bootSplashAssets';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';

type Props = {
  organizationName?: string | null;
  /** Mostrar isotipo + wordmark (sidebar web). En hub Más mobile conviene false. */
  showBrand?: boolean;
  /** Tap en nombre de org (multi-org switcher). */
  onOrganizationPress?: () => void;
  /** Contenido a la derecha (p. ej. botón cerrar en hub Más). */
  accessoryRight?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Sidebar teal del proto día 1 / desktop rail. */
  tone?: 'default' | 'onTeal';
};

/** Marca + nombre de organización; la sucursal activa vive en el rail (desktop) o chip del header (mobile). */
export function AppBrandBlock({
  organizationName,
  showBrand = true,
  onOrganizationPress,
  accessoryRight,
  style,
  tone = 'default',
}: Props) {
  const onTeal = tone === 'onTeal';
  const orgNode = organizationName ? (
    onOrganizationPress ? (
      <TouchableOpacity
        onPress={onOrganizationPress}
        accessibilityRole="button"
        accessibilityLabel={`Cambiar negocio: ${organizationName}`}
        testID="org-switcher.pill"
      >
        <Text style={[styles.organizationName, onTeal && styles.organizationNameOnTeal]} numberOfLines={2}>
          {organizationName}
        </Text>
      </TouchableOpacity>
    ) : (
      <Text style={[styles.organizationName, onTeal && styles.organizationNameOnTeal]} numberOfLines={2}>
        {organizationName}
      </Text>
    )
  ) : null;

  return (
    <View style={[styles.root, accessoryRight ? styles.rootWithAccessory : null, style]}>
      <View style={styles.textBlock}>
        {showBrand ? (
          <View style={styles.brandRow}>
            <Image source={VUELTO_NEGOCIO_ICON} style={styles.brandIcon} accessibilityIgnoresInvertColors accessibilityLabel="" />
            <Text style={[styles.brandTitle, onTeal && styles.brandTitleOnTeal]}>Vuelto Negocio</Text>
          </View>
        ) : null}
        {orgNode}
      </View>
      {accessoryRight ? <View style={styles.accessory}>{accessoryRight}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: spacing.xs,
  },
  rootWithAccessory: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  brandIcon: {
    width: 28,
    height: 28,
    borderRadius: 7,
  },
  accessory: {
    flexShrink: 0,
  },
  brandTitle: {
    ...typography.body,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  brandTitleOnTeal: {
    color: '#fff',
  },
  organizationName: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  organizationNameOnTeal: {
    color: 'rgba(255,255,255,0.78)',
  },
});
